// ═══════════════════════════════════════════════════════════
// Redaction Editor — PDF viewer with click-drag redaction
//
// Architecture:
//   - pdf.js renders each page to an off-screen canvas
//   - An overlay <canvas> on top captures mouse events
//   - Redaction boxes are stored in state (Redaction[])
//   - Each box requires an exemption code selection
//   - "Burn Redactions" calls the worker API to create
//     a new PDF with black boxes burned in (via pdf-lib)
//   - The result is saved as the redactedStorageUrl on the doc
//
// This component is opened from DocumentsTab via a route:
//   /staff/redact/:docId
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  IconArrowLeft, IconLoader2, IconCheck, IconTrash, IconZoomIn, IconZoomOut,
  IconChevronLeft, IconChevronRight,
} from '@tabler/icons-react';
import { getDocument, getOriginalDownloadUrl, uploadRedactedVersion } from '../../../api/foiaDocuments';
import { extractDocId, getCachedFile } from '../../../api/documents';
import {
  createRedaction, getRedactionsForDocument,
  type Redaction, type CreateRedactionInput,
} from '../../../api/redactions';
import { listExemptionCodes, type ExemptionCode } from '../../../api/jurisdiction';
import { suggestRedactions, type RedactionSuggestion } from '../../../api/ai';
import { useAuthStore } from '../../../store/authStore';
import type { FoiaDocument } from '../../../api/foiaDocuments';

const JURISDICTION_ID = import.meta.env.VITE_JURISDICTION_ID ?? '1';

interface DragBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

interface PendingRedaction {
  id: string;
  page: number;
  x: number; y: number;
  width: number; height: number;
  exemptionCode: string;
  saved: boolean;
}

// ── PDF Page Canvas ───────────────────────────────────────

function PDFPageCanvas({
  pageNum, pdfDoc, scale,
  redactions, hoveredId, onRedactionAdd,
}: {
  pageNum: number;
  pdfDoc: unknown; // pdfjsLib.PDFDocumentProxy
  scale: number;
  redactions: PendingRedaction[];
  hoveredId: string | null;
  onRedactionAdd: (r: Omit<PendingRedaction, 'id' | 'exemptionCode' | 'saved'>) => void;
}) {
  const canvasRef   = useRef<HTMLCanvasElement>(null);
  const overlayRef  = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [drag, setDrag] = useState<DragBox | null>(null);

  // Render PDF page
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const page = await (pdfDoc as { getPage: (n: number) => Promise<unknown> }).getPage(pageNum);
        const viewport = (page as { getViewport: (o: { scale: number }) => { width: number; height: number } }).getViewport({ scale });
        const w = Math.floor(viewport.width);
        const h = Math.floor(viewport.height);
        if (cancelled) return;
        setSize({ w, h });
        const canvas = canvasRef.current!;
        canvas.width  = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d')!;
        await (page as { render: (o: unknown) => { promise: Promise<void> } }).render({ canvasContext: ctx, viewport }).promise;
      } catch (err) {
        console.error('[PDFPageCanvas] page render error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [pdfDoc, pageNum, scale]);

  // Draw redaction overlays
  useEffect(() => {
    const canvas = overlayRef.current;
    if (!canvas || !size.w) return;
    canvas.width  = size.w;
    canvas.height = size.h;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, size.w, size.h);

    // Draw existing redactions
    for (const r of redactions.filter(r => r.page === pageNum)) {
      const isHovered = r.id === hoveredId;
      ctx.fillStyle = isHovered ? 'rgba(251,191,36,0.85)' : r.saved ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.5)';
      ctx.fillRect(r.x * scale, r.y * scale, r.width * scale, r.height * scale);
      ctx.strokeStyle = isHovered ? '#F59E0B' : r.saved ? '#000' : 'var(--cds-red-400)';
      ctx.lineWidth = isHovered ? 3 : 2;
      ctx.strokeRect(r.x * scale, r.y * scale, r.width * scale, r.height * scale);
    }

    // Draw drag preview
    if (drag) {
      const x = Math.min(drag.startX, drag.endX);
      const y = Math.min(drag.startY, drag.endY);
      const w = Math.abs(drag.endX - drag.startX);
      const h = Math.abs(drag.endY - drag.startY);
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = 'var(--cds-red-400)';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, w, h);
    }
  }, [redactions, drag, size, scale, pageNum, hoveredId]);

  function getPos(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = overlayRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    const { x, y } = getPos(e);
    setDrag({ startX: x, startY: y, endX: x, endY: y });
  }
  function onMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!drag) return;
    const { x, y } = getPos(e);
    setDrag(d => d ? { ...d, endX: x, endY: y } : null);
  }
  function onMouseUp() {
    if (!drag) return;
    const x = Math.min(drag.startX, drag.endX);
    const y = Math.min(drag.startY, drag.endY);
    const w = Math.abs(drag.endX - drag.startX);
    const h = Math.abs(drag.endY - drag.startY);
    setDrag(null);
    if (w < 8 || h < 8) return; // too small
    // Convert to unscaled coordinates
    onRedactionAdd({ page: pageNum, x: x / scale, y: y / scale, width: w / scale, height: h / scale });
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block', userSelect: 'none', boxShadow: 'none' }}>
      {/* Canvases must always be in the DOM so canvasRef is populated when the effect runs */}
      <canvas ref={canvasRef} style={{ display: size.w ? 'block' : 'none' }} />
      {!size.w && (
        <div style={{ width: 600, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--cds-gray-500)' }} />
        </div>
      )}
      <canvas
        ref={overlayRef}
        style={{ position: 'absolute', top: 0, left: 0, cursor: 'crosshair', display: size.w ? 'block' : 'none' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────

export default function RedactionEditor() {
  const { docId } = useParams<{ docId: string }>();
  const authUser  = useAuthStore(s => s.user);
  const actorEmail = authUser?.email ?? '';

  const [doc, setDoc]               = useState<FoiaDocument | null>(null);
  const [pdfDoc, setPdfDoc]         = useState<unknown>(null);
  const [numPages, setNumPages]     = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale]           = useState(1.2);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [loadError, setLoadError]   = useState<string | null>(null);
  const [exemptions, setExemptions] = useState<ExemptionCode[]>([]);
  const [redactions, setRedactions] = useState<PendingRedaction[]>([]);
  const [pendingBox, setPendingBox] = useState<Omit<PendingRedaction, 'id' | 'exemptionCode' | 'saved'> | null>(null);
  const [selectedExemption, setSelectedExemption] = useState('');
  const [saving, setSaving]         = useState(false);
  const [aiLoading, setAiLoading]   = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<(RedactionSuggestion & { sourcePage: number })[]>([]);
  const [aiSuggestRan, setAiSuggestRan] = useState(false);
  const [aiError, setAiError]       = useState<string | null>(null);
  const [aiAllPages, setAiAllPages] = useState(true);
  const [aiProgress, setAiProgress] = useState('');
  const [aiExemptionLoading, setAiExemptionLoading] = useState(false);
  const [aiPrompt, setAiPrompt]     = useState('');
  const [applyingAll, setApplyingAll] = useState(false);
  const [hoveredRedactionId, setHoveredRedactionId] = useState<string | null>(null);
  const [burning, setBurning]       = useState(false);
  const [burnProgress, setBurnProgress] = useState(0);
  const [burnComplete, setBurnComplete] = useState(false);
  const [burnError, setBurnError]   = useState<string | null>(null);

  useEffect(() => {
    if (!docId) return;
    const id = parseInt(docId, 10);
    if (isNaN(id)) return;

    setLoadingPdf(true);
    setLoadError(null);

    // Kick off all loads in parallel
    listExemptionCodes(JURISDICTION_ID).then(setExemptions);

    getRedactionsForDocument(id).then(existing => {
      setRedactions(existing.map(r => ({
        id: String(r.recordId),
        page: r.page,
        x: r.coordinates.x,
        y: r.coordinates.y,
        width: r.coordinates.width,
        height: r.coordinates.height,
        exemptionCode: r.exemptionCode,
        saved: true,
      })));
    });

    // Fetch document record → get signed download URL → load PDF.
    // cancelled flag prevents StrictMode's double-invocation from calling
    // setPdfDoc twice (which would cancel the PDFPageCanvas render mid-flight).
    let cancelled = false;

    (async () => {
      try {
        const docRecord = await getDocument(id);
        if (cancelled) return;
        if (!docRecord) throw new Error('Document record not found.');
        setDoc(docRecord);

        // If the file was uploaded in this session, load from ArrayBuffer
        // (avoids needing the download-url endpoint immediately after upload).
        const docUuid = extractDocId(docRecord.storageUrl);
        const cachedFile = docUuid ? getCachedFile(docUuid) : null;

        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.mjs',
          import.meta.url,
        ).toString();

        let pdfSource: string | { data: ArrayBuffer };
        if (cachedFile) {
          pdfSource = { data: await cachedFile.arrayBuffer() };
        } else {
          // Always load the original so re-burning after edits works correctly.
          // getStaffDownloadUrl would serve the already-burned flat image instead.
          const url = await getOriginalDownloadUrl(docRecord);
          if (cancelled) return;
          if (!url) throw new Error('Could not get a download URL for this document.');
          pdfSource = `/api/s3download?url=${encodeURIComponent(url)}`;
        }

        const pdf = await pdfjsLib.getDocument(pdfSource).promise;
        if (cancelled) return;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoadingPdf(false);
      }
    })();

    return () => { cancelled = true; };
  }, [docId]);

  function handleAddRedaction(box: Omit<PendingRedaction, 'id' | 'exemptionCode' | 'saved'>) {
    setPendingBox(box);
    setSelectedExemption(exemptions[0]?.code ?? '');
  }

  async function handleSaveRedaction() {
    if (!pendingBox || !selectedExemption) return;
    if (!docId) return;
    setSaving(true);
    try {
      const code = exemptions.find(e => e.code === selectedExemption);
      const input: CreateRedactionInput = {
        documentId:      parseInt(docId, 10),
        page:            pendingBox.page,
        coordinates:     { x: pendingBox.x, y: pendingBox.y, width: pendingBox.width, height: pendingBox.height },
        exemptionCode:   selectedExemption,
        exemptionDesc:   code?.title ?? selectedExemption,
        redactedByEmail: actorEmail,
      };
      const saved = await createRedaction(input);
      setRedactions(rs => [...rs, {
        id: String(saved.recordId),
        page: pendingBox.page,
        x: pendingBox.x, y: pendingBox.y,
        width: pendingBox.width, height: pendingBox.height,
        exemptionCode: selectedExemption,
        saved: true,
      }]);
      setPendingBox(null);
    } finally {
      setSaving(false);
    }
  }

  function handleDeleteRedaction(id: string) {
    setRedactions(rs => rs.filter(r => r.id !== id));
  }

  async function getPageText(pageNum: number): Promise<string> {
    if (!pdfDoc) return '';
    const page = await (pdfDoc as { getPage: (n: number) => Promise<unknown> }).getPage(pageNum);
    const content = await (page as {
      getTextContent: () => Promise<{ items: { str: string }[] }>;
    }).getTextContent();
    return content.items.map(item => item.str).join(' ');
  }

  /** Render a page to a canvas and return a base64 JPEG string.
   *  Sent to Claude alongside extracted text so it can read form fields,
   *  non-standard encoded text, and anything else the text layer misses. */
  async function getPageImage(pageNum: number): Promise<string> {
    if (!pdfDoc) return '';
    try {
      const page = await (pdfDoc as { getPage: (n: number) => Promise<unknown> }).getPage(pageNum);
      const viewport = (page as {
        getViewport: (o: { scale: number }) => { width: number; height: number };
      }).getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      canvas.width  = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const ctx = canvas.getContext('2d')!;
      await (page as { render: (o: unknown) => { promise: Promise<void> } })
        .render({ canvasContext: ctx, viewport }).promise;
      // Strip the data:image/jpeg;base64, prefix
      return canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
    } catch {
      return '';
    }
  }

  async function handleAiSuggest() {
    setAiLoading(true);
    setAiSuggestions([]);
    setAiError(null);
    setAiSuggestRan(false);
    setAiProgress('');

    const pagesToScan = aiAllPages
      ? Array.from({ length: numPages }, (_, i) => i + 1)
      : [currentPage];

    try {
      const allSuggestions: (RedactionSuggestion & { sourcePage: number })[] = [];

      for (const pageNum of pagesToScan) {
        if (pagesToScan.length > 1) {
          setAiProgress(`Analyzing page ${pageNum} of ${pagesToScan.length}…`);
        }
        const [text, pageImageBase64] = await Promise.all([
          getPageText(pageNum),
          getPageImage(pageNum),
        ]);

        const suggestions = await suggestRedactions({
          documentText: text,
          exemptionCodes: exemptions.map(e => ({ code: e.code, title: e.title })),
          customInstruction: aiPrompt.trim() || undefined,
          pageImageBase64: pageImageBase64 || undefined,
        });
        allSuggestions.push(...suggestions.map(s => ({ ...s, sourcePage: pageNum })));
      }

      if (allSuggestions.length === 0) {
        // Check synchronously whether any page had extractable text
        const texts = await Promise.all(pagesToScan.map(p => getPageText(p)));
        const hasText = texts.some(t => t.trim().length > 0);
        setAiError(hasText
          ? 'No suggestions found — try a more specific prompt, or draw boxes manually.'
          : 'No extractable text found. Try drawing boxes manually for image-based PDFs.'
        );
      }

      setAiSuggestions(allSuggestions);
      setAiSuggestRan(true);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI request failed.');
    } finally {
      setAiLoading(false);
      setAiProgress('');
    }
  }

  async function handleAiExemption() {
    setAiExemptionLoading(true);
    try {
      const [text, pageImageBase64] = await Promise.all([
        getPageText(currentPage),
        getPageImage(currentPage),
      ]);
      const suggestions = await suggestRedactions({
        documentText: text,
        exemptionCodes: exemptions.map(e => ({ code: e.code, title: e.title })),
        pageImageBase64: pageImageBase64 || undefined,
      });
      const sorted = [...suggestions].sort((a, b) => b.confidence - a.confidence);
      const best = sorted.find(s => exemptions.some(e => e.code === s.exemptionCode));
      if (best) setSelectedExemption(best.exemptionCode);
    } catch (err) {
      console.error('[AI exemption]', err);
    } finally {
      setAiExemptionLoading(false);
    }
  }

  async function handleApplySuggestion(suggestion: RedactionSuggestion & { sourcePage: number }) {
    if (!pdfDoc) return;

    const targetPage = suggestion.sourcePage;
    if (targetPage !== currentPage) setCurrentPage(targetPage);

    try {
      const page = await (pdfDoc as { getPage: (n: number) => Promise<unknown> }).getPage(targetPage);
      const viewport = (page as { getViewport: (o: { scale: number }) => { width: number; height: number } }).getViewport({ scale: 1 });
      const pageHeight = viewport.height;

      const textContent = await (page as {
        getTextContent: () => Promise<{ items: Array<{ str: string; transform: number[]; width: number; height: number }> }>;
      }).getTextContent();

      const items = textContent.items;

      // Build a full-text string tracking which character belongs to which item
      let fullText = '';
      const ranges: Array<{ start: number; end: number; item: typeof items[0] }> = [];
      for (const item of items) {
        if (!item.str) continue;
        ranges.push({ start: fullText.length, end: fullText.length + item.str.length, item });
        fullText += item.str;
      }

      // Normalize whitespace for matching
      const needle = suggestion.text.trim().replace(/\s+/g, ' ');
      const haystack = fullText.replace(/\s+/g, ' ');
      const idx = haystack.indexOf(needle);

      if (idx === -1) {
        // Text not found on this page — just open the pending box at page centre
        // so the user can at least see the suggestion context and place manually
        const cx = viewport.width / 4;
        const cy = pageHeight / 4;
        setPendingBox({ page: targetPage, x: cx, y: cy, width: viewport.width / 2, height: 20 });
        setSelectedExemption(suggestion.exemptionCode || exemptions[0]?.code || '');
        return;
      }

      const matchEnd = idx + needle.length;
      const matchingItems = ranges.filter(r => r.end > idx && r.start < matchEnd);
      if (matchingItems.length === 0) return;

      // Bounding box in PDF bottom-left coords → convert to canvas top-left
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const { item } of matchingItems) {
        const x = item.transform[4];
        const y = item.transform[5];
        const w = item.width;
        const h = Math.abs(item.transform[3]) || item.height || 12;
        const canvasX = x;
        const canvasY = pageHeight - y - h;
        minX = Math.min(minX, canvasX);
        minY = Math.min(minY, canvasY);
        maxX = Math.max(maxX, canvasX + w);
        maxY = Math.max(maxY, canvasY + h);
      }

      const pad = 3;
      setPendingBox({
        page: targetPage,
        x: minX - pad,
        y: minY - pad,
        width:  maxX - minX + pad * 2,
        height: maxY - minY + pad * 2,
      });
      setSelectedExemption(suggestion.exemptionCode || exemptions[0]?.code || '');
    } catch (err) {
      console.error('[apply suggestion]', err);
    }

    // Remove from suggestions list regardless of whether text was found
    setAiSuggestions(prev => prev.filter(s => s !== suggestion));
  }

  async function handleApplyAllSuggestions() {
    if (!pdfDoc || !docId || aiSuggestions.length === 0) return;
    setApplyingAll(true);
    const docIdNum = parseInt(docId, 10);

    try {
      // Group suggestions by source page so we only load each page's text once
      const byPage = new Map<number, typeof aiSuggestions>();
      for (const s of aiSuggestions) {
        const pg = s.sourcePage;
        if (!byPage.has(pg)) byPage.set(pg, []);
        byPage.get(pg)!.push(s);
      }

      const inputs: CreateRedactionInput[] = [];

      for (const [pageNum, pageSuggestions] of byPage) {
        const page = await (pdfDoc as { getPage: (n: number) => Promise<unknown> }).getPage(pageNum);
        const viewport = (page as { getViewport: (o: { scale: number }) => { width: number; height: number } }).getViewport({ scale: 1 });
        const pageHeight = viewport.height;

        const textContent = await (page as {
          getTextContent: () => Promise<{ items: Array<{ str: string; transform: number[]; width: number; height: number }> }>;
        }).getTextContent();
        const items = textContent.items;

        let fullText = '';
        const ranges: Array<{ start: number; end: number; item: typeof items[0] }> = [];
        for (const item of items) {
          if (!item.str) continue;
          ranges.push({ start: fullText.length, end: fullText.length + item.str.length, item });
          fullText += item.str;
        }

        for (const s of pageSuggestions) {
          const needle   = s.text.trim().replace(/\s+/g, ' ');
          const haystack = fullText.replace(/\s+/g, ' ');
          const code     = exemptions.find(e => e.code === s.exemptionCode);
          const exemptionCode = s.exemptionCode || exemptions[0]?.code || '';
          const exemptionDesc = code?.title ?? s.exemptionCode;

          const matchStarts: number[] = [];
          let searchFrom = 0;
          while (true) {
            const idx = haystack.indexOf(needle, searchFrom);
            if (idx === -1) break;
            matchStarts.push(idx);
            searchFrom = idx + needle.length;
          }

          if (matchStarts.length === 0) continue;

          for (const idx of matchStarts) {
            const matchEnd = idx + needle.length;
            const matchingItems = ranges.filter(r => r.end > idx && r.start < matchEnd);
            if (matchingItems.length === 0) continue;

            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for (const { item } of matchingItems) {
              const x = item.transform[4];
              const y = item.transform[5];
              const w = item.width;
              const h = Math.abs(item.transform[3]) || item.height || 12;
              minX = Math.min(minX, x);
              minY = Math.min(minY, pageHeight - y - h);
              maxX = Math.max(maxX, x + w);
              maxY = Math.max(maxY, pageHeight - y);
            }
            const pad = 3;
            inputs.push({
              documentId: docIdNum, page: pageNum,
              coordinates: { x: minX - pad, y: minY - pad, width: maxX - minX + pad * 2, height: maxY - minY + pad * 2 },
              exemptionCode, exemptionDesc, redactedByEmail: actorEmail,
            });
          }
        }
      }

      // Save in batches of 5 with a small delay between batches to avoid rate limiting
      const BATCH_SIZE = 5;
      const newRedactions: PendingRedaction[] = [];
      let failCount = 0;

      for (let i = 0; i < inputs.length; i += BATCH_SIZE) {
        const batch = inputs.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(batch.map(input => createRedaction(input)));
        for (let j = 0; j < results.length; j++) {
          const result = results[j];
          const input = batch[j];
          if (result.status === 'fulfilled') {
            const saved = result.value;
            newRedactions.push({
              id: String(saved.recordId),
              page: input.page,
              x: input.coordinates.x, y: input.coordinates.y,
              width: input.coordinates.width, height: input.coordinates.height,
              exemptionCode: input.exemptionCode,
              saved: true,
            });
          } else {
            failCount++;
            console.error('[apply all] failed:', result.reason);
          }
        }
        if (i + BATCH_SIZE < inputs.length) {
          await new Promise(r => setTimeout(r, 150)); // brief pause between batches
        }
      }

      setRedactions(rs => [...rs, ...newRedactions]);
      setAiSuggestions([]);
      if (failCount > 0) {
        setAiError(`${newRedactions.length} redactions saved. ${failCount} failed — try applying remaining suggestions individually.`);
      }
    } catch (err) {
      console.error('[apply all]', err);
      setAiError('Apply All failed. Try applying suggestions individually.');
    } finally {
      setApplyingAll(false);
    }
  }

  async function handleBurnRedactions() {
    if (!doc || !docId || !pdfDoc) return;
    const saved = redactions.filter(r => r.saved);
    if (saved.length === 0) return;

    setBurning(true);
    setBurnProgress(0);
    setBurnComplete(false);
    setBurnError(null);

    try {
      // Render-then-flatten approach: each page is rasterized to a canvas via
      // PDF.js, redaction boxes are drawn directly onto the pixel data, and the
      // result is assembled into a brand-new image-only PDF. Because we never
      // carry the original text layer forward, there is nothing to select or
      // copy through the black boxes.
      const RENDER_SCALE = 2; // 2× for print-quality output
      const { PDFDocument, PDFName } = await import('pdf-lib');
      const newPdf = await PDFDocument.create();

      const pdfProxy = pdfDoc as {
        getPage: (n: number) => Promise<unknown>;
        numPages: number;
      };

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        setBurnProgress(Math.round((pageNum / numPages) * 88));

        const page = await pdfProxy.getPage(pageNum);
        const viewport = (page as {
          getViewport: (o: { scale: number }) => { width: number; height: number };
        }).getViewport({ scale: RENDER_SCALE });

        const w = Math.floor(viewport.width);
        const h = Math.floor(viewport.height);

        // Render page pixels
        const canvas = document.createElement('canvas');
        canvas.width  = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d')!;
        await (page as {
          render: (o: unknown) => { promise: Promise<void> };
        }).render({ canvasContext: ctx, viewport }).promise;

        // Paint redaction boxes directly onto the rasterised pixels
        ctx.fillStyle = '#000000';
        for (const r of saved.filter(r => r.page === pageNum)) {
          ctx.fillRect(
            r.x      * RENDER_SCALE,
            r.y      * RENDER_SCALE,
            r.width  * RENDER_SCALE,
            r.height * RENDER_SCALE,
          );
        }

        // Canvas → JPEG bytes
        const dataUrl  = canvas.toDataURL('image/jpeg', 0.92);
        const b64      = dataUrl.split(',')[1];
        const imgBytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));

        // Embed image in new PDF at original (unscaled) page dimensions
        const jpgImage = await newPdf.embedJpg(imgBytes);
        const pdfPage  = newPdf.addPage([viewport.width / RENDER_SCALE, viewport.height / RENDER_SCALE]);
        pdfPage.drawImage(jpgImage, {
          x: 0, y: 0,
          width:  viewport.width  / RENDER_SCALE,
          height: viewport.height / RENDER_SCALE,
        });
      }

      // Strip metadata from the output document
      newPdf.setTitle('');
      newPdf.setAuthor('');
      newPdf.setSubject('');
      newPdf.setKeywords([]);
      newPdf.setProducer('OpenGov OpenRecords');
      newPdf.setCreator('OpenGov OpenRecords');
      newPdf.setCreationDate(new Date());
      newPdf.setModificationDate(new Date());
      try { newPdf.catalog.delete(PDFName.of('Metadata')); } catch { /* not present in all PDFs */ }

      setBurnProgress(90);
      const burned = await newPdf.save();
      const redactedFile = new File(
        [burned as BlobPart],
        doc.fileName.replace(/\.pdf$/i, '') + '_redacted.pdf',
        { type: 'application/pdf' },
      );

      await uploadRedactedVersion(
        parseInt(docId, 10),
        redactedFile,
        actorEmail,
        p => setBurnProgress(90 + Math.round(p * 0.1)),
      );

      const fresh = await getDocument(parseInt(docId, 10));
      if (fresh) setDoc(fresh);
      setBurnComplete(true);
      setBurnProgress(100);
    } catch (err) {
      setBurnError(err instanceof Error ? err.message : String(err));
    } finally {
      setBurning(false);
    }
  }

  const pageRedactions = redactions.filter(r => r.page === currentPage);

  return (
    <div style={{ padding: '20px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <Link
          to={doc?.isStandalone ? '/staff/redact' : `/staff/requests/${doc?.requestId}?tab=documents`}
          style={{ color: 'var(--color-primary-700)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3, fontSize: 14 }}
        >
          <IconArrowLeft size={13} /> {doc?.isStandalone ? 'Back to Redaction' : 'Back to Request'}
        </Link>
        <span style={{ color: 'var(--color-neutral-300)' }}>|</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)' }}>Redaction Editor</span>
        {numPages > 0 && <span style={{ fontSize: 14, color: 'var(--color-neutral-400)' }}>— {numPages} pages · {redactions.length} redaction{redactions.length !== 1 ? 's' : ''}</span>}
      </div>

      {loadingPdf ? (
        <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--color-neutral-400)' }}>
          <IconLoader2 size={28} style={{ animation: 'spin 1s linear infinite', marginBottom: 10 }} />
          <div style={{ fontSize: 14 }}>Loading document…</div>
        </div>
      ) : loadError ? (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: 'var(--cds-red-700)', marginBottom: 16 }}>{loadError}</div>
          <Link to={doc?.isStandalone ? '/staff/redact' : `/staff/requests/${doc?.requestId}?tab=documents`} className="btn btn-secondary btn-sm">
            ← {doc?.isStandalone ? 'Back to Redaction' : 'Back to Request'}
          </Link>
        </div>
      ) : !pdfDoc ? null : (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {/* PDF viewer */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Toolbar */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
              padding: '8px 12px', background: 'var(--color-neutral-50)',
              border: '1px solid var(--color-neutral-200)', borderRadius: 4,
            }}>
              <button className="btn btn-secondary btn-sm" style={{ padding: '3px 8px' }} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>
                <IconChevronLeft size={13} />
              </button>
              <span style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>
                Page {currentPage} / {numPages}
              </span>
              <button className="btn btn-secondary btn-sm" style={{ padding: '3px 8px' }} onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))} disabled={currentPage >= numPages}>
                <IconChevronRight size={13} />
              </button>
              <div style={{ flex: 1 }} />
              <button className="btn btn-secondary btn-sm" style={{ padding: '3px 8px' }} onClick={() => setScale(s => Math.min(3, s + 0.2))}>
                <IconZoomIn size={13} />
              </button>
              <span style={{ fontSize: 12, color: 'var(--color-neutral-500)', width: 36, textAlign: 'center' }}>
                {Math.round(scale * 100)}%
              </span>
              <button className="btn btn-secondary btn-sm" style={{ padding: '3px 8px' }} onClick={() => setScale(s => Math.max(0.5, s - 0.2))}>
                <IconZoomOut size={13} />
              </button>
              <div style={{ width: 1, height: 16, background: 'var(--color-neutral-200)', margin: '0 2px' }} />
              <button
                className="btn btn-primary btn-sm"
                style={{ padding: '3px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                onClick={handleBurnRedactions}
                disabled={burning || redactions.filter(r => r.saved).length === 0}
                title={redactions.filter(r => r.saved).length === 0 ? 'Save at least one redaction first' : 'Burn redactions into a new PDF and save as redacted version'}
              >
                {burning
                  ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                  : null}
                {burning ? `Burning${burnProgress > 0 ? ` ${burnProgress}%` : '…'}` : 'Burn Redactions'}
              </button>
            </div>

            <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 140px)', background: '#E5E7EB', padding: 24, borderRadius: 4 }}>
              <PDFPageCanvas
                pageNum={currentPage}
                pdfDoc={pdfDoc}
                scale={scale}
                redactions={redactions}
                hoveredId={hoveredRedactionId}
                onRedactionAdd={handleAddRedaction}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Instructions */}
            <div style={{ padding: '12px 14px', background: 'var(--cds-gray-50)', border: '1px solid var(--color-neutral-200)', borderRadius: 4 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: 6 }}>How to Redact</div>
              <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', lineHeight: 1.6 }}>
                Click and drag on the document to draw a redaction box. Select an exemption code and save.
              </div>
            </div>

            {/* AI prompt */}
            <div style={{ padding: '10px 12px', background: 'var(--cds-gray-50)', border: '1px solid var(--color-neutral-200)', borderRadius: 4 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: 6 }}>✦ AI Suggest</div>
              <textarea
                className="form-input"
                rows={2}
                style={{ fontSize: 12, resize: 'none', marginBottom: 6 }}
                placeholder="Optional focus: names, SSNs, account numbers, 'Project X'…"
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAiSuggest(); } }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--color-neutral-600)', cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={aiAllPages} onChange={e => setAiAllPages(e.target.checked)} style={{ cursor: 'pointer' }} />
                  All pages
                </label>
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ flex: 1, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                  onClick={handleAiSuggest}
                  disabled={aiLoading || !pdfDoc}
                >
                  {aiLoading ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                  {aiLoading ? (aiProgress || 'Analyzing…') : '✦ Run'}
                </button>
              </div>
              {aiError && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--cds-red-700)', lineHeight: 1.5 }}>{aiError}</div>
              )}
              {aiSuggestRan && aiSuggestions.length === 0 && !aiError && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-neutral-400)' }}>
                  No suggestions found — try a broader prompt.
                </div>
              )}
            </div>

            {/* AI suggestions */}
            {aiSuggestions.length > 0 && (
              <div className="card" style={{ display: 'flex', flexDirection: 'column', maxHeight: 340 }}>
                <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)' }}>
                    AI Suggestions
                  </span>
                  <span style={{ fontSize: 12, background: 'var(--color-primary-100)', color: 'var(--color-primary-700)', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
                    {aiSuggestions.length}
                  </span>
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 3 }}
                    onClick={handleApplyAllSuggestions}
                    disabled={applyingAll}
                    title="Save all suggestions as redactions at once"
                  >
                    {applyingAll ? <IconLoader2 size={10} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                    {applyingAll ? 'Applying…' : '✦ Apply All'}
                  </button>
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {aiSuggestions.map((s, i) => (
                    <div key={i} style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-neutral-100)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <code style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary-700)', background: 'var(--color-primary-100)', padding: '1px 5px', borderRadius: 3 }}>
                          {s.exemptionCode}
                        </code>
                        <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>p.{s.sourcePage}</span>
                        <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>
                          {Math.round(s.confidence * 100)}%
                        </span>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                          <button
                            className="btn btn-primary btn-sm"
                            style={{ padding: '1px 8px', fontSize: 12 }}
                            onClick={() => handleApplySuggestion(s)}
                          >
                            Apply
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '1px 6px', fontSize: 12 }}
                            title="Dismiss"
                            onClick={() => setAiSuggestions(prev => prev.filter(x => x !== s))}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', fontStyle: 'italic', marginBottom: 3 }}>
                        "{s.text.length > 60 ? s.text.slice(0, 60) + '…' : s.text}"
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>{s.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending box */}
            {pendingBox && (
              <div style={{ padding: '14px', background: 'white', border: '2px solid var(--color-primary-500)', borderRadius: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-800)', marginBottom: 10 }}>New Redaction — Page {pendingBox.page}</div>
                <div className="form-field" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <label className="form-label" style={{ marginBottom: 0 }}>Exemption Code *</label>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ padding: '1px 7px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 3 }}
                      onClick={handleAiExemption}
                      disabled={aiExemptionLoading || exemptions.length === 0}
                    >
                      {aiExemptionLoading
                        ? <IconLoader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />
                        : null}
                      {aiExemptionLoading ? 'Asking…' : '✦ Ask AI'}
                    </button>
                  </div>
                  <select className="form-input" value={selectedExemption} onChange={e => setSelectedExemption(e.target.value)}>
                    {exemptions.length === 0 && <option value="">No codes configured</option>}
                    {exemptions.map(e => (
                      <option key={e.code} value={e.code}>{e.code} — {e.title}</option>
                    ))}
                    {exemptions.length === 0 && <option value="other">Other</option>}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => setPendingBox(null)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" style={{ flex: 1, gap: 4 }} disabled={!selectedExemption || saving} onClick={handleSaveRedaction}>
                    {saving ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <><IconCheck size={11} /> Save</>}
                  </button>
                </div>
              </div>
            )}

            {/* Current page redactions */}
            {pageRedactions.length > 0 && (
              <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-neutral-100)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Page {currentPage} Redactions</span>
                  <span style={{ background: 'var(--color-primary-100)', color: 'var(--color-primary-700)', borderRadius: 4, padding: '1px 7px', fontSize: 12, fontWeight: 700 }}>{pageRedactions.length}</span>
                </div>
                <div style={{ overflowY: 'auto', maxHeight: 180 }}>
                  {pageRedactions.map(r => (
                    <div key={r.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 12px', borderBottom: '1px solid var(--color-neutral-100)',
                        background: hoveredRedactionId === r.id ? '#FFFBEB' : undefined,
                        cursor: 'default',
                      }}
                      onMouseEnter={() => setHoveredRedactionId(r.id)}
                      onMouseLeave={() => setHoveredRedactionId(null)}
                    >
                      <code style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary-700)', background: 'var(--color-primary-100)', padding: '1px 5px', borderRadius: 3 }}>{r.exemptionCode}</code>
                      <span style={{ fontSize: 12, color: 'var(--color-neutral-500)', flex: 1 }}>
                        {Math.round(r.width)}×{Math.round(r.height)} px
                      </span>
                      <button
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--cds-red-700)', padding: 2, flexShrink: 0 }}
                        onClick={() => handleDeleteRedaction(r.id)}
                      >
                        <IconTrash size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All redactions summary */}
            {redactions.length > 0 && (
              <div style={{ padding: '10px 12px', background: 'var(--cds-green-50)', border: '1px solid var(--cds-green-200)', borderRadius: 4, fontSize: 12, color: 'var(--cds-green-700)' }}>
                <IconCheck size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {redactions.filter(r => r.saved).length} redaction{redactions.filter(r => r.saved).length !== 1 ? 's' : ''} saved across all pages
              </div>
            )}

            {/* Burn status */}
            {burnComplete && (
              <div style={{ padding: '10px 12px', background: 'var(--cds-green-50)', border: '1px solid var(--cds-green-200)', borderRadius: 4, fontSize: 12, color: 'var(--cds-green-700)' }}>
                <IconCheck size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {doc?.isStandalone
                  ? 'Redacted PDF saved to your library.'
                  : 'Redacted PDF saved. Ready for supervisor approval and release.'
                }
              </div>
            )}
            {burnError && (
              <div style={{ padding: '10px 12px', background: '#FBEFED', border: '1px solid #F7D5D0', borderRadius: 4, fontSize: 12, color: 'var(--cds-red-700)' }}>
                Burn failed: {burnError}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
